import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn';

export interface ListRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Mono 11px fg4 label above the value (mobile status rows). */
  label?: ReactNode;
  /** 400 14px/1.3 fg. */
  title?: ReactNode;
  /** Mono 11px/1.4 fg4 under the title. */
  sub?: ReactNode;
  /** Right-hand slot (StatusPill, Switch, Select, → arrow). */
  trailing?: ReactNode;
  selected?: boolean;
  /** Render as a button (tap target). */
  onPress?: () => void;
  first?: boolean;
}

/** Mobile list row: min-height 48, 10/16 padding, 1 px top line; 50 px menu rows use `menu`. */
export function ListRow({ label, title, sub, trailing, selected, onPress, first, className, children, ...rest }: ListRowProps) {
  const Comp: any = onPress ? 'button' : 'div';
  return (
    <Comp
      type={onPress ? 'button' : undefined}
      onClick={onPress}
      className={cn(
        'flex min-h-12 w-full items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-left text-fg',
        onPress && 'cursor-pointer border-x-0 border-b-0 bg-transparent hover:bg-hover',
        selected && 'bg-selected',
        first && 'border-t-0',
        className,
      )}
      {...rest}
    >
      {children ?? (
        <div className="flex min-w-0 flex-col gap-1">
          {label && <span className="font-mono text-sm font-normal leading-[1.4] text-fg4">{label}</span>}
          {title && <span className="text-lg font-normal leading-[1.3] text-fg">{title}</span>}
          {sub && <span className="font-mono text-sm font-normal leading-[1.4] text-fg4">{sub}</span>}
        </div>
      )}
      {trailing}
    </Comp>
  );
}

/** Mobile menu row: 50 px, 400 14px, label left, meta + → right. */
export function MenuRow({ label, meta, onPress, className }: { label: ReactNode; meta?: ReactNode; onPress?: () => void; className?: string }) {
  return (
    <button type="button" onClick={onPress} className={cn('flex min-h-[50px] w-full items-center justify-between border-0 border-t border-line bg-transparent px-4 text-left text-lg font-normal text-fg hover:bg-hover', className)}>
      <span>{label}</span>
      <span className="flex items-center gap-2.5">
        {meta && <span className="meta text-fg4">{meta}</span>}
        <span className="text-fg5">→</span>
      </span>
    </button>
  );
}
